import React, { useState, useEffect } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { useSession } from "../lib/session";
import { listCompetitions } from "../lib/api";
import Logo, { Frank, Wordmark } from "./Logo";
import { initTheme, setTheme } from "../lib/theme";

/**
 * The tabs belong to a competition, not the society.
 *
 * A season is made of four things — what's on today, the rounds, the
 * board, the swindle — and each used to be three or four taps below a
 * society-level tab. Members and courses moved behind the avatar: they're
 * set up once and then left alone.
 */
const NAV = [
  { to: "today", label: "Today",
    d: "M3 10.5 12 4l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" },
  { to: "rounds", label: "Rounds",
    d: "M4 6h16M4 12h16M4 18h16" },
  { to: "board", label: "Leaderboard",
    d: "M5 20V10M12 20V4M19 20v-7" },
  { to: "swindle", label: "Swindle",
    d: "M4 7c0-1.1 3.6-2 8-2s8 .9 8 2-3.6 2-8 2-8-.9-8-2zM4 7v5c0 1.1 3.6 2 8 2s8-.9 8-2V7M4 12v5c0 1.1 3.6 2 8 2s8-.9 8-2v-5" },
];

const Icon = ({ d }) => <svg viewBox="0 0 24 24"><path d={d} /></svg>;

export default function Layout() {
  const { society, societies, leaveSociety, profile,
          isAdmin, isOrganiser, isPlatformAdmin, signOut } = useSession();
  const [mode, setMode] = useState(() => initTheme());
  const flip = () => setMode(setTheme(mode === "dark" ? "light" : "dark"));

  /**
   * The competition in the path IS the context. Everything society-level
   * sits behind the avatar, and the tabs only appear once you're inside
   * a competition — on the picker there is nothing to tab between.
   */
  const { pathname } = useLocation();
  const inComp = pathname.match(/^\/c\/([^/]+)/);
  /**
   * The competition you were last in stays the context while you're on a
   * society page, so the tabs don't vanish the moment you tap Members and
   * leave you with no way back in.
   */
  const [sticky, setSticky] = useState(
    () => { try { return sessionStorage.getItem("postcard.comp"); } catch { return null; } });
  const cid = inComp?.[1] ?? sticky;

  useEffect(() => {
    if (!inComp) return;
    setSticky(inComp[1]);
    try { sessionStorage.setItem("postcard.comp", inComp[1]); } catch { /* private mode */ }
  }, [inComp?.[1]]);
  const [comp, setComp] = useState(null);
  const [comps, setComps] = useState([]);

  useEffect(() => {
    if (!society?.id) return;
    listCompetitions(society.id).then(setComps).catch(() => setComps([]));
  }, [society?.id]);

  useEffect(() => {
    const found = cid ? comps.find((c) => c.id === cid) ?? null : null;
    setComp(found);
    if (cid && comps.length && !found) {
      setSticky(null);
      try { sessionStorage.removeItem("postcard.comp"); } catch { /* ignore */ }
    }
  }, [cid, comps]);

  const tab = (n) => (
    <NavLink key={n.to} to={`/c/${cid}/${n.to}`}
             className={({ isActive }) => (isActive ? "on" : undefined)}>
      <Icon d={n.d} />
      {n.label}
    </NavLink>
  );

  return (
    <div className="shell">
      <aside className="rail">
        <div className="brand">
          <Logo size={26} ink="#fff" wordSize="1.15rem" />
        </div>

        <div className="rail-soc">
          <span className="eyebrow" style={{ color: "rgba(255,255,255,.5)" }}>Society</span>
          <strong>{society?.name}</strong>
          <div className="row" style={{ gap: ".7rem" }}>
            <button className="btn-link" onClick={leaveSociety}>
              {societies.length > 1 ? "Switch" : "Societies"}
            </button>
            {isAdmin && <Link to="/society" className="btn-link">Settings</Link>}
          </div>
        </div>

        {comp && (
          <nav>
            {NAV.map((n) => (
              <NavLink key={n.to} to={`/c/${cid}/${n.to}`}
                       className={({ isActive }) => (isActive ? "on" : undefined)}>
                {n.label}
              </NavLink>
            ))}
          </nav>
        )}

        <nav className="rail-society">
          {[["/competitions", "Competitions", true, true],
            ["/members", "Members", isOrganiser, false],
            ["/courses", "Courses", isOrganiser, false],
            ["/society", "Society", isOrganiser, false],
            ["/platform", "Site", isPlatformAdmin, false]]
            .filter(([, , show]) => show)
            .map(([to, label, , end]) => (
              <NavLink key={to} to={to} end={end}
                       className={({ isActive }) => (isActive ? "on" : undefined)}>
                {label}
              </NavLink>
            ))}
        </nav>

        <div className="rail-foot">
          <Link to="/account" className="rail-account">
            <span className="avatar">
              {(profile?.full_name ?? "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
            </span>
            <span style={{ minWidth: 0 }}>
              <strong>{profile?.full_name}</strong>
              <small>Your account</small>
            </span>
          </Link>
          <div className="row" style={{ justifyContent: "space-between" }}>
            <button className="btn-link" onClick={signOut}>Sign out</button>
            <button className="mode-toggle" onClick={flip}>
              {mode === "dark" ? "Light" : "Dark"}
            </button>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          {comp ? (
            /* which competition you're in, and the way to another */
            <Link to="/competitions" className="topbar-comp">
              <strong>{comp.name}</strong>
              <span className="caret-down" aria-hidden="true" />
            </Link>
          ) : (
            <Link to="/" className="topbar-brand" aria-label="PostcardGolf home">
              <Frank s={24} ink="#fff" />
              <Wordmark size="1.0625rem" ink="#fff" />
            </Link>
          )}
          <span className="spacer" />
          {/* Configuring the society is an organiser's job, so the cog only
              exists for them. A player has nothing behind it. */}
          {isOrganiser && (
            <Link to="/society" className="cog" aria-label="Society settings">
              <svg viewBox="0 0 24 24" width="19" height="19" fill="none"
                   stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" />
              </svg>
            </Link>
          )}
          <Link to="/account" className="avatar" aria-label="Your account">
            {(profile?.full_name ?? "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
          </Link>
        </header>

        <div className="page"><Outlet /></div>

        {comp && <nav className="tabbar">{NAV.map(tab)}</nav>}
      </div>
    </div>
  );
}
